import React, { useState } from 'react';
import { buildInitialSections, normalizeSections, methodUid as uid } from '../data/methods';
import styles from './Method.module.css';

export default function Method({ sections, setSections, pendingSections, onSectionsApplied }) {
  // Apply imported sections when they arrive from RecipeImport
  React.useEffect(() => {
    if (!pendingSections || pendingSections.length === 0) return;
    setSections(normalizeSections(pendingSections));
    onSectionsApplied && onSectionsApplied();
  }, [pendingSections]); // eslint-disable-line

  // ── Section actions ────────────────────────────────────────
  const addSection = () => {
    setSections(prev => [...prev, {
      id: uid(),
      key: 'custom_' + uid(),
      title: 'New Section',
      color: '#546E7A',
      steps: [{ id: uid(), text: '' }],
    }]);
  };

  const removeSection = (secId) => {
    setSections(prev => prev.filter(s => s.id !== secId));
  };

  const moveSection = (secId, dir) => {
    setSections(prev => {
      const idx  = prev.findIndex(s => s.id === secId);
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const updateSectionTitle = (secId, title) => {
    setSections(prev => prev.map(s =>
      s.id === secId ? { ...s, title } : s
    ));
  };

  // ── Step actions ───────────────────────────────────────────
  const addStep = (secId) => {
    setSections(prev => prev.map(s =>
      s.id === secId
        ? { ...s, steps: [...s.steps, { id: uid(), text: '' }] }
        : s
    ));
  };

  const removeStep = (secId, stepId) => {
    setSections(prev => prev.map(s => {
      if (s.id !== secId) return s;
      if (s.steps.length <= 1) return s; // keep at least one step
      return { ...s, steps: s.steps.filter(st => st.id !== stepId) };
    }));
  };

  const updateStep = (secId, stepId, text) => {
    setSections(prev => prev.map(s =>
      s.id === secId
        ? { ...s, steps: s.steps.map(st =>
            st.id === stepId ? { ...st, text } : st
          )}
        : s
    ));
  };

  const moveStep = (secId, stepId, dir) => {
    setSections(prev => prev.map(s => {
      if (s.id !== secId) return s;
      const idx  = s.steps.findIndex(st => st.id === stepId);
      const next = [...s.steps];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return s;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return { ...s, steps: next };
    }));
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all instructions to defaults? Your changes will be lost.')) {
      setSections(buildInitialSections());
    }
  };

  return (
    <div className={styles.method}>

      {/* Header row */}
      <div className={styles.methodHeader}>
        <div className={styles.methodTitle}>Method & Instructions</div>
        <div className={styles.methodActions}>
          <button className={styles.btnGhost} onClick={resetToDefaults}>
            Reset to defaults
          </button>
          <button className={styles.btnOutline} onClick={addSection}>
            + Add section
          </button>
        </div>
      </div>

      <p className={styles.methodSubtitle}>
        Customize your step-by-step instructions. These are saved with your recipe.
      </p>

      {/* Sections */}
      <div className={styles.sections}>
        {sections.map((sec, idx) => (
          <InstructionSection
            key={sec.id}
            sec={sec}
            index={idx}
            total={sections.length}
            onUpdateTitle={updateSectionTitle}
            onRemoveSection={removeSection}
            onMoveSection={moveSection}
            onAddStep={addStep}
            onRemoveStep={removeStep}
            onUpdateStep={updateStep}
            onMoveStep={moveStep}
          />
        ))}
      </div>

    </div>
  );
}

/* ── Single section ─────────────────────────────────────────── */
function InstructionSection({
  sec, index, total,
  onUpdateTitle, onRemoveSection, onMoveSection,
  onAddStep, onRemoveStep, onUpdateStep, onMoveStep,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);

  return (
    <div className={styles.section} style={{ '--sec-color': sec.color }}>

      {/* Section header */}
      <div className={styles.secHeader}>
        <div className={styles.secLeft}>
          <div className={styles.secDot} />
          {editingTitle ? (
            <input
              className={styles.titleInput}
              value={sec.title}
              onChange={e => onUpdateTitle(sec.id, e.target.value)}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={e => e.key === 'Enter' && setEditingTitle(false)}
              autoFocus
            />
          ) : (
            <span
              className={styles.secTitle}
              onClick={() => setEditingTitle(true)}
              title="Click to rename"
            >
              {sec.title}
            </span>
          )}
          <span className={styles.stepCount}>
            {sec.steps.length} step{sec.steps.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className={styles.secRight}>
          <button
            className={styles.secBtn}
            onClick={() => onMoveSection(sec.id, -1)}
            disabled={index === 0}
            title="Move section up"
          >↑</button>
          <button
            className={styles.secBtn}
            onClick={() => onMoveSection(sec.id, 1)}
            disabled={index === total - 1}
            title="Move section down"
          >↓</button>
          <button
            className={styles.secBtn}
            onClick={() => onAddStep(sec.id)}
            title="Add step"
          >
            + step
          </button>
          <button
            className={styles.secBtn}
            onClick={() => setEditingTitle(true)}
            title="Rename section"
          >
            ✎
          </button>
          <button
            className={`${styles.secBtn} ${styles.secBtnDanger}`}
            onClick={() => onRemoveSection(sec.id)}
            title="Remove section"
          >
            ✕
          </button>
          <button
            className={styles.collapseBtn}
            onClick={() => setCollapsed(c => !c)}
          >
            {collapsed ? '›' : '⌄'}
          </button>
        </div>
      </div>

      {/* Steps */}
      {!collapsed && (
        <div className={styles.steps}>
          {sec.steps.map((step, idx) => (
            <Step
              key={step.id}
              step={step}
              index={idx}
              total={sec.steps.length}
              secId={sec.id}
              onUpdate={onUpdateStep}
              onRemove={onRemoveStep}
              onMove={onMoveStep}
            />
          ))}

          <button
            className={styles.addStepInline}
            onClick={() => onAddStep(sec.id)}
          >
            + Add step
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Single step ────────────────────────────────────────────── */
function Step({ step, index, total, secId, onUpdate, onRemove, onMove }) {
  return (
    <div className={styles.step}>
      {/* Step number */}
      <div className={styles.stepNum}>{index + 1}</div>

      {/* Step text */}
      <textarea
        className={styles.stepText}
        value={step.text}
        placeholder="Describe this step…"
        onChange={e => onUpdate(secId, step.id, e.target.value)}
        rows={Math.max(2, ((step.text || '').match(/\n/g) || []).length + 2)}
      />

      {/* Step controls */}
      <div className={styles.stepControls}>
        <button
          className={styles.stepBtn}
          onClick={() => onMove(secId, step.id, -1)}
          disabled={index === 0}
          title="Move up"
        >↑</button>
        <button
          className={styles.stepBtn}
          onClick={() => onMove(secId, step.id, 1)}
          disabled={index === total - 1}
          title="Move down"
        >↓</button>
        <button
          className={`${styles.stepBtn} ${styles.stepBtnRemove}`}
          onClick={() => onRemove(secId, step.id)}
          disabled={total <= 1}
          title="Remove step"
        >✕</button>
      </div>
    </div>
  );
}
